import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { RefreshCw, Link2, CheckCircle, XCircle, Loader2, Download, Upload, Trash2, Settings, Plus, Activity, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import WondeIntegration from './WondeIntegration';

const PLATFORM_INFO = {
  taptile: { name: 'Taptile', color: 'purple', defaultUrl: 'https://taptile.app' },
  ubots: { name: 'ubots', color: 'blue', defaultUrl: '' },
  wonde: { name: 'Wonde', color: 'green', defaultUrl: 'https://api.wonde.com' },
  generic: { name: 'Custom API', color: 'slate', defaultUrl: '' },
};

// ---- Sync Health helpers ----
const relativeTime = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'never';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatInterval = (min) => {
  const m = Number(min) || 60;
  if (m < 60) return `every ${m} minutes`;
  if (m === 60) return 'every hour';
  if (m < 1440) return `every ${m / 60} hours`;
  return 'every 24 hours';
};

const nextSyncEstimate = (lastIso, intervalMin) => {
  if (!lastIso) return 'on next check (within a minute)';
  const last = new Date(lastIso).getTime();
  if (isNaN(last)) return 'on next check';
  const next = last + (Number(intervalMin) || 60) * 60 * 1000;
  const diff = next - Date.now();
  if (diff <= 0) return 'due now';
  const mins = Math.ceil(diff / 60000);
  if (mins < 60) return `in about ${mins} minute${mins === 1 ? '' : 's'}`;
  const hrs = Math.round(mins / 60);
  return `in about ${hrs} hour${hrs === 1 ? '' : 's'}`;
};

const ContactSyncSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(null); // 'push' | 'import' | null
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);

  const [formData, setFormData] = useState({
    platform: 'taptile',
    api_url: '',
    api_key: '',
    auto_sync_enabled: false,
    sync_interval_minutes: 60
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/contact-sync/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const openAddForm = (platform) => {
    const info = PLATFORM_INFO[platform] || PLATFORM_INFO.generic;
    setFormData({
      platform: platform || 'taptile',
      api_url: info.defaultUrl || '',
      api_key: '',
      auto_sync_enabled: false,
      sync_interval_minutes: 60
    });
    setShowAddForm(true);
    setEditingPlatform(null);
    setTestResult(null);
  };

  const openEditForm = (integration) => {
    setFormData({
      platform: integration.platform,
      api_url: integration.api_url || '',
      api_key: '',
      auto_sync_enabled: integration.auto_sync_enabled || false,
      sync_interval_minutes: integration.sync_interval_minutes || 60
    });
    setEditingPlatform(integration.platform);
    setShowAddForm(true);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!formData.api_url) {
      toast.error('API URL is required');
      return;
    }
    if (!formData.api_key && !editingPlatform) {
      toast.error('API Key is required');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.api_key && editingPlatform) {
        const existing = (settings?.integrations || []).find(i => i.platform === editingPlatform);
        if (existing?.api_key) payload.api_key = existing.api_key;
      }
      await api.post('/contact-sync/settings', payload);
      toast.success(`${PLATFORM_INFO[formData.platform]?.name || formData.platform} integration saved`);
      setShowAddForm(false);
      setEditingPlatform(null);
      setTestResult(null);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.api_url || !formData.api_key) {
      toast.error('API URL and Key are required to test');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/contact-sync/test-connection', formData);
      setTestResult(response.data);
      if (response.data.status === 'ok') toast.success(response.data.message);
      else toast.error(response.data.message);
    } catch (error) {
      setTestResult({ status: 'error', message: 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async (platform, direction) => {
    setSyncing(direction);
    setSyncResult(null);
    const endpoint = direction === 'push' ? `/contact-sync/push/${platform}` : `/contact-sync/import/${platform}`;
    try {
      const response = await api.post(endpoint);
      setSyncResult(response.data);
      toast.success(response.data.message);
      fetchSettings();
    } catch (error) {
      const msg = error.response?.data?.detail || `${direction} failed`;
      toast.error(msg);
      setSyncResult({ status: 'error', message: msg });
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (platform) => {
    const name = PLATFORM_INFO[platform]?.name || platform;
    if (!window.confirm(`Remove ${name} integration? This will not delete any synced contacts.`)) return;
    try {
      await api.delete(`/contact-sync/settings/${platform}`);
      toast.success(`${name} integration removed`);
      setSyncResult(null);
      fetchSettings();
    } catch (error) {
      toast.error('Failed to remove integration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-indigo-600" />
      </div>
    );
  }

  const integrations = settings?.integrations || [];
  const isEnabled = settings?.contact_sync_enabled;

  if (!isEnabled) {
    return (
      <div className="space-y-6" data-testid="contact-sync-not-enabled">
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200" data-testid="contact-sync-disabled">
          <Link2 size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Contact Sync Not Enabled</h3>
          <p className="text-slate-500 mb-4">Contact your NexusCMS administrator to enable integrations for your account.</p>
        </div>
        {/* Wonde is shown independently of contact_sync_enabled */}
        <WondeIntegration />
      </div>
    );
  }

  // Platforms not yet connected
  const connectedPlatforms = integrations.map(i => i.platform);
  const availablePlatforms = Object.keys(PLATFORM_INFO).filter(p => !connectedPlatforms.includes(p));

  return (
    <div className="space-y-6" data-testid="contact-sync-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link2 size={20} className="text-indigo-600" />
            <h3 className="text-lg font-semibold text-slate-800">Integrations</h3>
          </div>
          <p className="text-sm text-slate-500">Connect external platforms to sync contacts.</p>
        </div>
        {availablePlatforms.length > 0 && !showAddForm && (
          <Select onValueChange={(v) => openAddForm(v)}>
            <SelectTrigger className="w-48" data-testid="add-integration-btn">
              <Plus size={16} className="mr-2" />
              <SelectValue placeholder="Add Integration" />
            </SelectTrigger>
            <SelectContent>
              {availablePlatforms.map(p => (
                <SelectItem key={p} value={p}>{PLATFORM_INFO[p].name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Connected Integrations */}
      {integrations.map((integ) => {
        const info = PLATFORM_INFO[integ.platform] || PLATFORM_INFO.generic;
        const colorClass = {
          purple: 'border-purple-200 bg-purple-50',
          blue: 'border-blue-200 bg-blue-50',
          green: 'border-green-200 bg-green-50',
          slate: 'border-slate-200 bg-slate-50',
        }[info.color] || 'border-slate-200 bg-slate-50';

        return (
          <div key={integ.platform} className={`rounded-lg border p-5 ${colorClass}`} data-testid={`integration-${integ.platform}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-600" />
                  <span className="font-semibold text-slate-800">{info.name}</span>
                  {integ.auto_sync_enabled && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Auto-sync</span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{integ.api_url}</p>
                <p className="text-xs text-slate-400 mt-0.5">Key: {integ.api_key_masked}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditForm(integ)} data-testid={`edit-${integ.platform}`}>
                  <Settings size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(integ.platform)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`remove-${integ.platform}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>

            {/* Sync Health panel */}
            {(() => {
              const res = integ.last_sync_result || null;
              const hasError = !!res && (res.errors > 0 || res.error);
              const neverSynced = !integ.last_sync_at;
              const statusLabel = neverSynced ? 'Never synced' : hasError ? 'Needs attention' : 'Healthy';
              const statusStyle = neverSynced
                ? 'bg-slate-100 text-slate-600'
                : hasError
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700';
              const StatusIcon = neverSynced ? Clock : hasError ? AlertTriangle : CheckCircle;
              return (
                <div className="bg-white/70 rounded-lg border border-slate-200 p-4 mb-3" data-testid={`sync-health-${integ.platform}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Activity size={16} className="text-indigo-600" />
                      <span className="text-sm font-semibold text-slate-800">Sync Health</span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${statusStyle}`}
                      data-testid={`sync-status-${integ.platform}`}>
                      <StatusIcon size={12} /> {statusLabel}
                    </span>
                  </div>

                  {/* Last sync line */}
                  <div className="text-sm text-slate-600 mb-3">
                    {neverSynced ? (
                      <span>No sync has run yet. Use “Push to {info.name}” below or wait for the scheduled sync.</span>
                    ) : (
                      <span>
                        Last sync <span className="font-medium text-slate-800">{relativeTime(integ.last_sync_at)}</span>
                        {res?.auto ? (
                          <span className="ml-1 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">automatic</span>
                        ) : (
                          <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">manual</span>
                        )}
                        <span className="block text-xs text-slate-400 mt-0.5">{new Date(integ.last_sync_at).toLocaleString()}</span>
                      </span>
                    )}
                  </div>

                  {/* Stat chips */}
                  {res && (
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="rounded-md bg-green-50 border border-green-100 px-3 py-2 text-center" data-testid={`stat-created-${integ.platform}`}>
                        <div className="text-lg font-semibold text-green-700">{res.created ?? 0}</div>
                        <div className="text-xs text-slate-500">New</div>
                      </div>
                      <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-center" data-testid={`stat-updated-${integ.platform}`}>
                        <div className="text-lg font-semibold text-blue-700">{res.updated ?? 0}</div>
                        <div className="text-xs text-slate-500">Updated</div>
                      </div>
                      <div className={`rounded-md px-3 py-2 text-center border ${(res.errors > 0) ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`} data-testid={`stat-errors-${integ.platform}`}>
                        <div className={`text-lg font-semibold ${(res.errors > 0) ? 'text-red-700' : 'text-slate-400'}`}>{res.errors ?? 0}</div>
                        <div className="text-xs text-slate-500">Errors</div>
                      </div>
                    </div>
                  )}

                  {/* Error detail */}
                  {res?.error && (
                    <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2 mb-2" data-testid={`sync-error-${integ.platform}`}>
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{res.error}</span>
                    </div>
                  )}

                  {/* Schedule line */}
                  <div className="flex items-center gap-2 text-xs text-slate-500 pt-1 border-t border-slate-100 mt-2">
                    <Clock size={13} />
                    {integ.auto_sync_enabled ? (
                      <span data-testid={`next-sync-${integ.platform}`}>
                        Auto-sync is <span className="font-medium text-slate-700">on</span> ({formatInterval(integ.sync_interval_minutes)}). Next sync {nextSyncEstimate(integ.last_auto_sync_at || integ.last_sync_at, integ.sync_interval_minutes)}.
                      </span>
                    ) : (
                      <span>Auto-sync is off — contacts sync only when you press “Push”.</span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Sync Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => handleSync(integ.platform, 'push')}
                disabled={syncing !== null}
                data-testid={`push-${integ.platform}`}
              >
                {syncing === 'push' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                Push to {info.name}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => handleSync(integ.platform, 'import')}
                disabled={syncing !== null}
                data-testid={`import-${integ.platform}`}
              >
                {syncing === 'import' ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
                Import from {info.name}
              </Button>
            </div>
          </div>
        );
      })}

      {/* Sync Result */}
      {syncResult && (
        <div className={`rounded-lg p-4 border ${syncResult.status === 'ok' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`} data-testid="sync-result">
          <p className="font-medium text-sm mb-1">{syncResult.status === 'ok' ? 'Sync Complete' : 'Sync Failed'}</p>
          <p className="text-sm">{syncResult.message}</p>
          {syncResult.result && (
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-green-700">Created: {syncResult.result.created}</span>
              <span className="text-blue-700">Updated: {syncResult.result.updated}</span>
              {syncResult.result.skipped > 0 && <span className="text-slate-500">Skipped: {syncResult.result.skipped}</span>}
              {syncResult.result.errors > 0 && <span className="text-red-600">Errors: {syncResult.result.errors}</span>}
              {syncResult.result.total_sent && <span className="text-slate-500">Total sent: {syncResult.result.total_sent}</span>}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm text-slate-700">
              {editingPlatform ? `Edit ${PLATFORM_INFO[editingPlatform]?.name || editingPlatform}` : 'New Connection'}
            </span>
            <Button variant="ghost" size="sm" onClick={() => { setShowAddForm(false); setEditingPlatform(null); setTestResult(null); }}>
              Cancel
            </Button>
          </div>

          {!editingPlatform && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Platform</Label>
              <Select value={formData.platform} onValueChange={(v) => {
                const info = PLATFORM_INFO[v] || {};
                setFormData({ ...formData, platform: v, api_url: info.defaultUrl || formData.api_url });
              }}>
                <SelectTrigger data-testid="platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availablePlatforms.map(p => (
                    <SelectItem key={p} value={p}>{PLATFORM_INFO[p].name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">API Base URL</Label>
            <Input
              placeholder="https://taptile.app"
              value={formData.api_url}
              onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
              data-testid="api-url-input"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500 mb-1 block">
              API Key {editingPlatform && <span className="text-slate-400">(leave blank to keep existing)</span>}
            </Label>
            <Input
              type="password"
              placeholder={editingPlatform ? 'Leave blank to keep existing key' : 'Paste your API key'}
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              data-testid="api-key-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm font-medium text-slate-700">Auto-Sync</Label>
                <p className="text-xs text-slate-400">Push subscribers on a schedule</p>
              </div>
              <Switch
                checked={formData.auto_sync_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_sync_enabled: checked })}
                data-testid="auto-sync-toggle"
              />
            </div>
            {formData.auto_sync_enabled && (
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Interval</Label>
                <Select value={String(formData.sync_interval_minutes)} onValueChange={(v) => setFormData({ ...formData, sync_interval_minutes: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">Every 30 min</SelectItem>
                    <SelectItem value="60">Every hour</SelectItem>
                    <SelectItem value="360">Every 6 hours</SelectItem>
                    <SelectItem value="1440">Every 24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {testResult && (
            <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${testResult.status === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {testResult.status === 'ok' ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {testResult.message}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleTestConnection} disabled={testing || !formData.api_url || !formData.api_key} data-testid="test-connection-btn">
              {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
              Test Connection
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.api_url} data-testid="save-integration-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              {editingPlatform ? 'Update' : 'Connect'}
            </Button>
          </div>
        </div>
      )}

      {/* Wonde MIS Integration — dedicated section, independent of contact_sync_enabled */}
      <div className="pt-2 border-t border-slate-200">
        <WondeIntegration />
      </div>
    </div>
  );
};

export default ContactSyncSettings;
